import { isEmpty } from 'lodash/fp';
import { createReducer } from 'typesafe-actions';

import {
    AI_CHAT_MESSAGE_KIND,
    AI_CHAT_MESSAGE_ROLE,
    isAiChatDataCollectionPickerMessage,
    isAiChatQueryResultMessage,
} from '@portal/common/models/ai-chat-message';
import { OPERATION_STATUS } from '@portal/common/models/operation-status';
import { OperationStatus } from '@portal/common/types';

import { ANALYTICS_SOURCE_AI_CHAT } from '../../models/analytics-source';
import {
    applyDataExplorerInitializationPlan,
    dataSelectionValidation,
    queryDataAsync,
} from '../data-explorer/actions';
import { changeDataCollection } from '../user-settings/actions';
import {
    addAssistantDataCollectionPicker,
    addAssistantFilters,
    addAssistantMessage,
    addAssistantQueryResult,
    cancelChatMessage,
    chatDataCollectionSwitchApplied,
    chatRequestFailed,
    dismissChatDataCollectionPicker,
    manualFiltersPosted,
    pickChatDataCollection,
    queryResultFailed,
    queryResultLoaded,
    resetChat,
    resetFeedbackSubmission,
    setActiveChat,
    setChatAutoDataCollection,
    setChatId,
    setManualFiltersSyncEnabled,
    setMessageReaction,
    submitChatMessage,
    submitFeedback,
    submitFeedbackFailed,
    submitFeedbackSucceeded,
} from './actions';
import {
    AiChatDataCollectionPickerMessage,
    AiChatFeedbackSubmission,
    AiChatMessage,
    AiChatQueryResultMessage,
} from './types';
import {
    buildDataCollectionPickerMessage,
    canMirrorQueryResultToDataExplorer,
    getAlternativeDataCollectionOptions,
    getLastFilterBearingMessage,
    isEmptyQueryResult,
    isFeedbackableMessage,
    isOpenDataCollectionPickerMessage,
} from './utils';

interface State {
    messages: AiChatMessage[];
    requestStatus: OperationStatus;
    chatId: string | null;
    dataCollectionId: number | null;
    isAutoDataCollection: boolean;
    lastPickedDataCollectionId: number | null;
    feedbackSubmission: AiChatFeedbackSubmission;
    isManualFiltersSyncEnabled: boolean;
    // Until Data Explorer queries what the chat applied, its queried filters are older.
    isAwaitingMirror: boolean;
}

const initialFeedbackSubmission: AiChatFeedbackSubmission = {
    status: OPERATION_STATUS.IDLE,
    messageId: null,
    errorMessage: null,
};

const updateMessageById = <T extends AiChatMessage>(
    messages: AiChatMessage[],
    id: string,
    guard: (message: AiChatMessage) => message is T,
    update: (message: T) => AiChatMessage
): AiChatMessage[] =>
    messages.map((message) => (message.id === id && guard(message) ? update(message) : message));

const dismissOpenPickers = (messages: AiChatMessage[]): AiChatMessage[] =>
    messages.map((message) =>
        isOpenDataCollectionPickerMessage(message) ? { ...message, isDismissed: true } : message
    );

const markPickerAsPicked = (
    messages: AiChatMessage[],
    responseId: string,
    dataCollectionId: number
): AiChatMessage[] =>
    messages.map((message) =>
        isAiChatDataCollectionPickerMessage(message) && message.responseId === responseId
            ? { ...message, pickedDataCollectionId: dataCollectionId }
            : message
    );

const withoutEmptyDataCollectionOption = (message: AiChatQueryResultMessage) =>
    isEmptyQueryResult(message)
        ? getAlternativeDataCollectionOptions(message)
        : message.dataCollectionOptions;

const initialState: State = {
    messages: [],
    requestStatus: OPERATION_STATUS.IDLE,
    chatId: null,
    dataCollectionId: null,
    isAutoDataCollection: true,
    lastPickedDataCollectionId: null,
    feedbackSubmission: initialFeedbackSubmission,
    isManualFiltersSyncEnabled: true,
    isAwaitingMirror: false,
};

const resetConversationState = (state: State): State => ({
    ...state,
    messages: [],
    requestStatus: OPERATION_STATUS.IDLE,
    chatId: null,
    dataCollectionId: null,
    lastPickedDataCollectionId: null,
    feedbackSubmission: initialFeedbackSubmission,
    isAwaitingMirror: false,
});

const isAwaitingQueryResultMirror = (
    state: State,
    messages: AiChatMessage[],
    id: string
): boolean => {
    const latest = getLastFilterBearingMessage(messages);

    return latest?.id === id ? canMirrorQueryResultToDataExplorer(latest) : state.isAwaitingMirror;
};

const insertManualFiltersMessage = (
    messages: AiChatMessage[],
    promptId: string,
    manualMessage: AiChatMessage
): AiChatMessage[] => {
    const promptIndex = messages.findIndex((message) => message.id === promptId);

    if (promptIndex < 0) {
        return messages;
    }

    return [...messages.slice(0, promptIndex), manualMessage, ...messages.slice(promptIndex)];
};

export default createReducer(initialState as State)
    .handleAction(submitChatMessage, (state, action) => ({
        ...state,
        messages: [
            ...dismissOpenPickers(state.messages),
            {
                id: action.payload.id,
                role: AI_CHAT_MESSAGE_ROLE.USER,
                kind: AI_CHAT_MESSAGE_KIND.TEXT,
                content: action.payload.content,
            },
        ],
        requestStatus: OPERATION_STATUS.PENDING,
    }))
    .handleAction(cancelChatMessage, (state) => ({
        ...state,
        // Messages stay in the thread; only the pending wait is abandoned.
        requestStatus: OPERATION_STATUS.IDLE,
    }))
    .handleAction(addAssistantMessage, (state, action) => ({
        ...state,
        messages: [
            ...state.messages,
            {
                role: AI_CHAT_MESSAGE_ROLE.ASSISTANT,
                kind: AI_CHAT_MESSAGE_KIND.TEXT,
                reaction: null,
                ...action.payload,
            },
        ],
        requestStatus: OPERATION_STATUS.IDLE,
    }))
    .handleAction(addAssistantFilters, (state, action) => ({
        ...state,
        messages: [
            ...state.messages,
            {
                role: AI_CHAT_MESSAGE_ROLE.ASSISTANT,
                kind: AI_CHAT_MESSAGE_KIND.FILTERS,
                reaction: null,
                ...action.payload,
            },
        ],
        requestStatus: OPERATION_STATUS.IDLE,
    }))
    .handleAction(addAssistantDataCollectionPicker, (state, action) => ({
        ...state,
        messages: [...state.messages, buildDataCollectionPickerMessage(action.payload)],
        requestStatus: OPERATION_STATUS.IDLE,
    }))
    .handleAction(pickChatDataCollection, (state, action) => ({
        ...state,
        lastPickedDataCollectionId: action.payload.dataCollectionId,
        messages: updateMessageById(
            state.messages,
            action.payload.messageId,
            isAiChatDataCollectionPickerMessage,
            (message): AiChatDataCollectionPickerMessage => ({
                ...message,
                pickedDataCollectionId: action.payload.dataCollectionId,
            })
        ),
    }))
    .handleAction(changeDataCollection, (state, action) =>
        isEmpty(state.messages)
            ? state
            : { ...state, lastPickedDataCollectionId: action.payload.dataCollectionId }
    )
    .handleAction(chatDataCollectionSwitchApplied, (state, action) => {
        const { responseId, dataCollectionId, dataTypes, filters } = action.payload;
        const switched = updateMessageById(
            state.messages,
            responseId,
            isAiChatQueryResultMessage,
            (message): AiChatQueryResultMessage => ({
                ...message,
                dataCollectionId,
                dataTypes,
                filters,
                dataCollectionOptions: withoutEmptyDataCollectionOption(message),
                status: OPERATION_STATUS.PENDING,
                data: null,
                error: null,
                reaction: null,
            })
        );
        const messages = markPickerAsPicked(switched, responseId, dataCollectionId);

        return {
            ...state,
            messages,
            lastPickedDataCollectionId: dataCollectionId,
            isAwaitingMirror:
                getLastFilterBearingMessage(messages)?.id === responseId || state.isAwaitingMirror,
        };
    })
    .handleAction(dismissChatDataCollectionPicker, (state, action) => ({
        ...state,
        messages: updateMessageById(
            state.messages,
            action.payload,
            isAiChatDataCollectionPickerMessage,
            (message): AiChatDataCollectionPickerMessage => ({ ...message, isDismissed: true })
        ),
    }))
    .handleAction(addAssistantQueryResult, (state, action) => ({
        ...state,
        messages: [
            ...state.messages,
            {
                role: AI_CHAT_MESSAGE_ROLE.ASSISTANT,
                kind: AI_CHAT_MESSAGE_KIND.QUERY_RESULT,
                reaction: null,
                status: OPERATION_STATUS.PENDING,
                data: null,
                error: null,
                ...action.payload,
            },
        ],
        requestStatus: OPERATION_STATUS.IDLE,
        isAwaitingMirror: true,
    }))
    .handleAction(queryResultLoaded, (state, action) => {
        const messages = updateMessageById(
            state.messages,
            action.payload.id,
            isAiChatQueryResultMessage,
            (message) => ({
                ...message,
                status: OPERATION_STATUS.SUCCEEDED,
                data: action.payload.data,
                error: null,
            })
        );

        return {
            ...state,
            messages,
            isAwaitingMirror: isAwaitingQueryResultMirror(state, messages, action.payload.id),
        };
    })
    .handleAction(queryResultFailed, (state, action) => {
        const messages = updateMessageById(
            state.messages,
            action.payload.id,
            isAiChatQueryResultMessage,
            (message) => ({
                ...message,
                status: OPERATION_STATUS.FAILED,
                error: action.payload.error,
            })
        );

        return {
            ...state,
            messages,
            isAwaitingMirror: isAwaitingQueryResultMirror(state, messages, action.payload.id),
        };
    })
    .handleAction(setMessageReaction, (state, action) => ({
        ...state,
        messages: updateMessageById(
            state.messages,
            action.payload.id,
            isFeedbackableMessage,
            (message) => ({ ...message, reaction: action.payload.reaction })
        ),
    }))
    .handleAction(submitFeedback, (state, action) => ({
        ...state,
        feedbackSubmission: {
            status: OPERATION_STATUS.PENDING,
            messageId: action.payload.responseId,
            errorMessage: null,
        },
    }))
    .handleAction(submitFeedbackSucceeded, (state, action) => ({
        ...state,
        messages: updateMessageById(
            state.messages,
            action.payload.responseId,
            isFeedbackableMessage,
            (message) => ({ ...message, reaction: action.payload.reaction })
        ),
        feedbackSubmission: {
            status: OPERATION_STATUS.SUCCEEDED,
            messageId: action.payload.responseId,
            errorMessage: null,
        },
    }))
    .handleAction(submitFeedbackFailed, (state, action) => ({
        ...state,
        feedbackSubmission: {
            ...state.feedbackSubmission,
            status: OPERATION_STATUS.FAILED,
            errorMessage: action.payload.errorMessage,
        },
    }))
    .handleAction(resetFeedbackSubmission, (state) => ({
        ...state,
        feedbackSubmission: initialFeedbackSubmission,
    }))
    .handleAction(chatRequestFailed, (state, action) => ({
        ...state,
        messages: [
            ...state.messages,
            {
                id: action.payload.id,
                kind: AI_CHAT_MESSAGE_KIND.ERROR,
                content: action.payload.content,
                failedInput: action.payload.failedInput,
            },
        ],
        requestStatus: OPERATION_STATUS.FAILED,
    }))
    .handleAction(setChatId, (state, action) => ({
        ...state,
        chatId: action.payload,
    }))
    .handleAction(setChatAutoDataCollection, (state, action) => ({
        ...state,
        isAutoDataCollection: action.payload,
    }))
    .handleAction(setManualFiltersSyncEnabled, (state, action) => ({
        ...state,
        isManualFiltersSyncEnabled: action.payload,
    }))
    .handleAction(applyDataExplorerInitializationPlan, (state, action) =>
        action.payload.querySource === ANALYTICS_SOURCE_AI_CHAT
            ? { ...state, isAwaitingMirror: true }
            : state
    )
    // Dispatched after the queried state is set, or instead of it when the mirror is invalid.
    .handleAction([queryDataAsync.request, dataSelectionValidation.failure], (state) =>
        state.isAwaitingMirror ? { ...state, isAwaitingMirror: false } : state
    )
    .handleAction(manualFiltersPosted, (state, action) => {
        const { id, promptId, manualFilters } = action.payload;

        return {
            ...state,
            messages: insertManualFiltersMessage(state.messages, promptId, {
                id,
                role: AI_CHAT_MESSAGE_ROLE.MANUAL,
                kind: AI_CHAT_MESSAGE_KIND.MANUAL_FILTERS,
                dataTypes: manualFilters.dataTypes,
                filters: manualFilters.filters,
                visualization: manualFilters.visualization,
            }),
        };
    })
    .handleAction(resetChat, resetConversationState)
    .handleAction(setActiveChat, (state, action) => ({
        ...resetConversationState(state),
        chatId: action.payload.chatId,
        messages: action.payload.messages,
        dataCollectionId: action.payload.dataCollectionId,
        isAwaitingMirror: getLastFilterBearingMessage(action.payload.messages) != null,
    }));
